import React, { type FunctionComponent } from "react";
import { Table, Tag } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  ClientImportAggregation,
  type ProductClientsImportAggregation,
} from "@fitness-app/data-models/entities/ProductClientToImport";

import { isKnownImportSource } from "./importPresentation";
import { useProductImports } from "./useProductImports";

interface OwnProps {
  productId: string;
}

export const ProductImportsList: FunctionComponent<OwnProps> = ({ productId }) => {
  const { t } = useTranslation("products");
  const { data, isLoading } = useProductImports(productId);

  return (
    <Table<ProductClientsImportAggregation>
      size="small"
      rowKey="importId"
      loading={isLoading}
      dataSource={data ?? []}
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
      locale={{ emptyText: t("import.list.empty") }}
    >
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.list.columns.createdAt")}
        dataIndex="createdAt"
        key="createdAt"
        render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
      />
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.list.columns.source")}
        dataIndex="source"
        key="source"
        render={(source: string) => (isKnownImportSource(source) ? t(`import.source.${source}`) : source)}
      />
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.list.columns.status")}
        dataIndex="status"
        key="status"
        render={(status: ClientImportAggregation) => (
          <Tag color={status === ClientImportAggregation.Finished ? "green" : "processing"}>
            {t(`import.aggregationStatus.${status}`)}
          </Tag>
        )}
      />
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.summary.total")}
        dataIndex="total"
        key="total"
        align="right"
      />
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.summary.imported")}
        dataIndex="imported"
        key="imported"
        align="right"
      />
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.summary.skipped")}
        dataIndex="skipped"
        key="skipped"
        align="right"
        render={(skipped: number) => (skipped ? <span className="text-yellow-600">{skipped}</span> : skipped)}
      />
      <Table.Column<ProductClientsImportAggregation>
        title={t("import.summary.failed")}
        dataIndex="failed"
        key="failed"
        align="right"
        render={(failed: number) => (failed ? <span className="text-red-600">{failed}</span> : failed)}
      />
      <Table.Column<ProductClientsImportAggregation>
        key="details"
        render={(_, row) => (
          <Link to={`/products/${productId}/clients/imports/${row.importId}`}>{t("import.list.details")}</Link>
        )}
      />
    </Table>
  );
};
